import type {AuthProvider as RefineAuthProvider} from '@refinedev/core';
import axios from 'axios';

import {parseJwt} from '@app/helpers/parse-jwt';
import {Token} from '@app/types';
import {AppPermissions} from '@providers/security/auth-provider-types';
import {WorkspaceList} from '@workspaces/workspaces';

const API_URL = import.meta.env.VITE_API_URL;

type AuthProvider = Omit<RefineAuthProvider, 'getPermissions'> & {
  getPermissions: () => AppPermissions;
};

export const authProvider: AuthProvider = {
  login: async ({credential}: CredentialResponse) => {
    if (!credential) {
      return {
        success: false,
        error: {
          message: "Informations d'identification non valides",
          name: 'Erreur de connexion',
        },
      };
    }

    const response = await axios.get(`${API_URL}/token/exchange`, {
      headers: {
        Authorization: `Bearer ${credential}`,
      },
    });

    const token = response.data.token;
    const profileObj = parseJwt<UserPayload>(credential);

    localStorage.setItem(
      'user',
      JSON.stringify({
        ...parseJwt<Token>(token),
        avatar: profileObj.picture,
      }),
    );

    localStorage.setItem('token', `${token}`);

    return {
      success: true,
      redirectTo: '/',
    };
  },
  logout: async () => {
    const token = localStorage.getItem('token');

    if (token && typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      axios.defaults.headers.common = {};
      window.google?.accounts.id.revoke(token, () => {
        return {};
      });
    }

    return {
      success: true,
      redirectTo: '/login',
      successNotification: {
        message: 'Vous avez été déconnecté',
        type: 'success',
      },
    };
  },
  onError: async (error) => {
    if (error.response?.status === 401) {
      return {
        logout: true,
      };
    }

    return {error};
  },
  check: async () => {
    const token = localStorage.getItem('token');

    if (token) {
      const jwt = parseJwt<Token>(token);
      const isExp = jwt.exp * 1000 < Date.now();

      if (!isExp) {
        return {
          authenticated: true,
        };
      }
    }

    return {
      authenticated: false,
      error: {
        message: 'Vérification échouée',
        name: 'Non authentifié',
      },
      logout: true,
      redirectTo: '/login',
    };
  },
  getPermissions: async (): AppPermissions => {
    const token = localStorage.getItem('token');

    if (token) {
      const jwt = parseJwt<Token>(token);
      return {
        role: jwt.roles[0],
        workspaces: [WorkspaceList.GLOBAL, ...jwt.workspaces],
      };
    }

    return null;
  },
  getIdentity: async () => {
    const user = localStorage.getItem('user');
    if (user) {
      return JSON.parse(user);
    }

    return null;
  },
};
